import {
  AsyncSelectView,
  AsyncSelectViewProps,
  MultiAsyncSelectView,
  MultiAsyncSelectViewProps,
  useAsyncData,
  useAsyncSelectState,
  useMultiAsyncData,
  useMultiAsyncSelectState,
} from '@porsche-code/kado-ui';
import { SelectChangeEventDetail } from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import {
  PersonByIdQuery,
  PersonsQuery,
  PersonsQueryVariables,
  usePersonByIdQuery,
  usePersonsQuery,
} from '@/graphql';

type Data =
  | NonNullable<PersonsQuery['persons']['docs'][number]>
  | NonNullable<PersonByIdQuery['person']>;
type Filter = NonNullable<PersonsQueryVariables['filter']>;

export const PersonSelect = ({
  dataFilter,
  groupBy,
  ...props
}: Omit<AsyncSelectViewProps, 'data' | 'selectState' | 'i18n' | 'onChange'> & {
  dataFilter?: Filter;
  groupBy?: (d: Data) => string;
  onChange?: (
    e: CustomEvent<SelectChangeEventDetail>,
    person?: Data | null,
  ) => void;
}) => {
  const { t } = useTranslation(NAMESPACES);

  const queryClient = useQueryClient();

  const { enabled, searchString, selectState } = useAsyncSelectState(
    props.autoSelectSingleOption,
  );

  const selectData = useData({
    enabled,
    filter: dataFilter,
    searchString,
    value:
      props.value === null || props.value === undefined
        ? undefined
        : String(props.value),
    groupByFn: groupBy,
    appendixFn: appendix,
  });

  return (
    <AsyncSelectView
      {...props}
      data={selectData}
      selectState={selectState}
      i18n={{
        noData: t('common:noData'),
        error: t('common:error'),
      }}
      onChange={(e) => {
        const { value } = e.target as HTMLElement & { value: string };

        const person = value
          ? (queryClient.getQueryData<PersonByIdQuery>(
              usePersonByIdQuery.getKey({ id: Number(value) }),
            )?.person ??
            queryClient
              .getQueriesData<PersonsQuery>({
                exact: false,
                queryKey: usePersonsQuery.getKey(),
              })
              .flatMap(([, data]) => data?.persons.docs ?? [])
              .find((opt) => opt.id.toString() === value))
          : null;

        props.onChange?.(e, person);
      }}
    />
  );
};

const useData = ({
  enabled,
  filter,
  searchString,
  value,
  groupByFn,
  appendixFn,
}: {
  enabled?: boolean;
  filter?: Filter;
  searchString?: string;
  value?: string;
  groupByFn?: (d: Data) => string;
  appendixFn?: (d: Data) => string;
}) => {
  const {
    data: persons,
    isPending,
    isLoading,
    error: personsError,
  } = usePersonsQuery(
    {
      filter,
      searchString,
    },
    {
      enabled,
      select: (data) => data.persons.docs,
    },
  );

  const { data: person, error: personError } = usePersonByIdQuery(
    {
      id: Number(value),
    },
    {
      enabled: Boolean(value),
      initialData: value
        ? {
            person: persons?.find((opt) => opt.id.toString() === value),
          }
        : undefined,
      select: (data) => data.person,
    },
  );

  const { options, optgroups } = useAsyncData({
    data: persons,
    selected: person,
    searchString,
    equalsFn: (a, b) => a.id === b.id,
    toOptionFn: (d) => ({ label: d.name, value: d.id.toString() }),
    groupByFn,
    appendixFn,
  });

  return {
    options,
    optgroups,
    selectedOption: person
      ? { label: person.name, value: person.id.toString() }
      : undefined,

    isPending,
    isLoading,
    error: personsError ?? personError,
  };
};

export const MultiPersonSelect = ({
  dataFilter,
  groupBy,
  ...props
}: Omit<MultiAsyncSelectViewProps, 'data' | 'selectState' | 'i18n'> & {
  dataFilter?: Filter;
  groupBy?: (d: Data) => string;
}) => {
  const { t } = useTranslation(NAMESPACES);

  const { enabled, searchString, selectState } = useMultiAsyncSelectState();

  const selectData = useMultiData({
    enabled,
    filter: dataFilter,
    searchString,
    value:
      props.value === null || props.value === undefined
        ? undefined
        : props.value.map((v) => String(v)),
    groupByFn: groupBy,
    appendixFn: appendix,
  });

  return (
    <MultiAsyncSelectView
      {...props}
      data={selectData}
      selectState={selectState}
      i18n={{
        noData: t('common:noData'),
        error: t('common:error'),
      }}
    />
  );
};

const appendix = (d: Data) => ('organization' in d ? d.organization.name : '');

const useMultiData = ({
  enabled,
  filter,
  searchString,
  value,
  groupByFn,
  appendixFn,
}: {
  enabled?: boolean;
  filter?: Filter;
  searchString?: string;
  value?: string[];
  groupByFn?: (d: Data) => string;
  appendixFn?: (d: Data) => string;
}) => {
  const {
    data: persons,
    isPending,
    isLoading,
    error: personsError,
  } = usePersonsQuery(
    {
      filter,
      searchString,
    },
    {
      enabled,
      select: (data) => data.persons.docs,
    },
  );

  const { data: personsSelected, error: personError } = usePersonsQuery(
    {
      filter: { _id: { _in: value?.map(Number) } },
    },
    {
      enabled: Boolean(value),
      initialData: value
        ? {
            persons: {
              docs:
                persons?.filter((opt) => value.includes(opt.id.toString())) ??
                [],
            },
          }
        : undefined,
      select: (data) => data.persons.docs,
    },
  );

  const { options, optgroups } = useMultiAsyncData({
    data: persons,
    selected: personsSelected,
    searchString,
    equalsFn: (a, b) => a.id === b.id,
    toOptionFn: (d) => ({ label: d.name, value: d.id.toString() }),
    groupByFn,
    appendixFn,
  });

  return {
    options,
    optgroups,
    selectedOptions: personsSelected
      ? personsSelected.map((person) => ({
          label: person.name,
          value: person.id.toString(),
        }))
      : undefined,

    isPending,
    isLoading,
    error: personsError ?? personError,
  };
};
